import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SectionHeading from "@/components/SectionHeading";
import { WHATSAPP_URL, WHATSAPP_DISPLAY, EMAIL, contactSubjects } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { nome: "", telefone: "", email: "", assunto: "", mensagem: "", lgpd_consent: false };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lgpd_consent) {
      toast.error("É necessário aceitar o aviso de privacidade (LGPD) para enviar.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || "Erro ao enviar");
      toast.success("Mensagem enviada com sucesso! Obrigada pelo contato.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Não foi possível enviar agora. Tente pelo WhatsApp ou e-mail.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors focus:border-brand-royal focus:ring-2 focus:ring-brand-royal/20";

  return (
    <section id="contato" data-testid="contact-section" className="bg-slate-50 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          chapter="09"
          kicker="Contato"
          title="Fale com a nossa equipe"
          description="Sua opinião constrói esse mandato. Envie sua mensagem, sugestão de proposta ou peça uma agenda."
        />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-500 text-white"><MessageCircle className="h-6 w-6" /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">WhatsApp</span>
                <span className="block font-display text-lg font-bold text-slate-900">{WHATSAPP_DISPLAY}</span>
              </span>
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-card"
              className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-royal text-white"><Mail className="h-6 w-6" /></span>
              <span className="min-w-0">
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">E-mail</span>
                <span className="block break-all font-display text-base font-bold text-slate-900">{EMAIL}</span>
              </span>
            </a>
            <div className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="contact-region-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-orange text-white"><MapPin className="h-6 w-6" /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">Região</span>
                <span className="block font-display text-lg font-bold text-slate-900">Eunápolis · Extremo Sul da Bahia</span>
              </span>
            </div>
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm" data-testid="contact-map">
              <iframe
                title="Mapa da região de Eunápolis, Extremo Sul da Bahia"
                src="https://www.google.com/maps?q=Eun%C3%A1polis,+Bahia,+Brasil&output=embed"
                className="h-64 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl sm:p-9"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-700">Nome *</span>
                <input required data-testid="contact-input-nome" value={form.nome} onChange={set("nome")} placeholder="Seu nome completo" className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-700">Telefone *</span>
                <input required data-testid="contact-input-telefone" value={form.telefone} onChange={set("telefone")} placeholder="(73) 90000-0000" className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-700">E-mail *</span>
                <input required type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} placeholder="voce@email.com" className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-700">Assunto *</span>
                <select required data-testid="contact-select-assunto" value={form.assunto} onChange={set("assunto")} className={inputCls}>
                  <option value="" disabled>Selecione um assunto</option>
                  {contactSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-sm font-bold text-slate-700">Mensagem *</span>
                <textarea required rows={5} data-testid="contact-input-mensagem" value={form.mensagem} onChange={set("mensagem")} placeholder="Escreva sua mensagem, sugestão ou pedido de agenda..." className={`${inputCls} resize-none`} />
              </label>
            </div>

            <label className="mt-5 flex items-start gap-3 text-xs leading-relaxed text-slate-500" data-testid="contact-lgpd-label">
              <input
                type="checkbox"
                data-testid="contact-lgpd-checkbox"
                checked={form.lgpd_consent}
                onChange={set("lgpd_consent")}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-brand-royal"
              />
              <span>
                Autorizo o uso dos meus dados exclusivamente para retorno do meu contato, nos termos da{" "}
                <strong className="text-slate-700">Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)</strong>.
                Seus dados não serão compartilhados com terceiros.
              </span>
            </label>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-royal to-brand-dark px-8 py-4 text-base font-bold text-white shadow-xl shadow-brand-royal/30 transition-all duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {sending ? "Enviando..." : "Enviar mensagem"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
